import { useState, useRef, useEffect } from 'react';
import { Info } from 'lucide-react';
import { clsx } from 'clsx';

interface InfoTooltipProps {
  content: string;
  position?: 'top' | 'bottom' | 'left' | 'right';
  className?: string;
}

export function InfoTooltip({ content, position = 'top', className }: InfoTooltipProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [alignRight, setAlignRight] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isVisible && buttonRef.current) {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      const tooltipWidth = 256; // w-64 = 16rem = 256px
      const spaceOnRight = window.innerWidth - buttonRect.right;
      const spaceOnLeft = buttonRect.left;

      // If tooltip would overflow on the right, align it to the right
      if (spaceOnRight < tooltipWidth / 2 + 16) {
        setAlignRight(true);
      } else if (spaceOnLeft < tooltipWidth / 2 + 16) {
        setAlignRight(false);
      } else {
        setAlignRight(false);
      }
    }
  }, [isVisible]);

  const getPositionClasses = () => {
    if (position === 'top' || position === 'bottom') {
      const vertical = position === 'top' ? 'bottom-full mb-2' : 'top-full mt-2';
      const horizontal = alignRight ? 'right-0' : 'left-1/2 -translate-x-1/2';
      return `${vertical} ${horizontal}`;
    }

    const positionClasses = {
      left: 'right-full top-1/2 -translate-y-1/2 mr-2',
      right: 'left-full top-1/2 -translate-y-1/2 ml-2',
    };
    return positionClasses[position];
  };

  const getArrowClasses = () => {
    if (position === 'top' || position === 'bottom') {
      const vertical = position === 'top'
        ? 'top-full border-t-[var(--color-bg-tertiary)] border-x-transparent border-b-transparent'
        : 'bottom-full border-b-[var(--color-bg-tertiary)] border-x-transparent border-t-transparent';
      const horizontal = alignRight ? 'right-2' : 'left-1/2 -translate-x-1/2';
      return `${vertical} ${horizontal}`;
    }

    const arrowClasses = {
      left: 'left-full top-1/2 -translate-y-1/2 border-l-[var(--color-bg-tertiary)] border-y-transparent border-r-transparent',
      right: 'right-full top-1/2 -translate-y-1/2 border-r-[var(--color-bg-tertiary)] border-y-transparent border-l-transparent',
    };
    return arrowClasses[position];
  };

  return (
    <div className={clsx('relative inline-flex', className)}>
      <button
        ref={buttonRef}
        type="button"
        onMouseEnter={() => setIsVisible(true)}
        onMouseLeave={() => setIsVisible(false)}
        onFocus={() => setIsVisible(true)}
        onBlur={() => setIsVisible(false)}
        className="text-[var(--color-text-muted)] hover:text-[var(--color-text-secondary)] transition-colors p-0.5 cursor-pointer"
        aria-label="More information"
      >
        <Info className="h-3 w-3" />
      </button>

      {isVisible && (
        <div
          ref={tooltipRef}
          className={clsx(
            'absolute z-50 w-64 p-3 rounded-lg',
            'bg-[var(--color-bg-tertiary)] border border-[var(--color-border)]',
            'shadow-lg shadow-black/20',
            'text-sm text-[var(--color-text-secondary)] leading-relaxed',
            'animate-in fade-in-0 zoom-in-95 duration-200',
            getPositionClasses()
          )}
        >
          {content}
          <div
            className={clsx(
              'absolute w-0 h-0 border-[6px]',
              getArrowClasses()
            )}
          />
        </div>
      )}
    </div>
  );
}

// Predefined tooltips for consistent messaging across the app
export const TOOLTIPS = {
  // Dashboard metrics
  stocksAnalyzed: "The total number of stocks our AI is currently tracking and analyzing for sentiment patterns.",
  buyOpportunities: "Stocks where LOW sentiment (fear/pessimism) meets STRONG fundamentals - the gap between them creates a contrarian buying opportunity with limited downside.",
  sellSignals: "Stocks where HIGH sentiment (euphoria/greed) signals the stock is stretched and priced for perfection - only downside risk from here, regardless of fundamentals.",
  postsAnalyzed: "Total social media posts and news articles our AI has read to generate these insights.",

  // Score explanations (all 0-100 scale)
  sentimentScore: "How positive or negative people feel about this stock online. LOW sentiment (fear) + strong fundamentals = buying opportunity. HIGH sentiment (euphoria) = stretched/overvalued.",
  fundamentalScore: "How financially healthy the company is based on profits, growth, and debt. Higher is better (0-100). We want STRONG fundamentals paired with LOW sentiment for the best opportunities.",
  disconnectScore: "Measures the GAP between fundamentals and sentiment. Above 50 = fundamentals stronger than sentiment (potential BUY). Below 50 = sentiment stronger than fundamentals (potential SELL).",

  // Sentiment breakdown
  bullishPercentage: "The percentage of posts that are positive or optimistic about this stock.",
  bearishPercentage: "The percentage of posts that are negative or pessimistic about this stock.",
  neutralPercentage: "The percentage of posts that don't express a strong opinion either way.",

  // Fundamental metrics - Valuation
  peRatio: "Price-to-Earnings ratio - how much you pay for each dollar the company earns. Lower often means cheaper, but depends on industry.",
  forwardPE: "Expected P/E ratio based on projected future earnings. Lower suggests the stock may be undervalued.",
  pegRatio: "P/E ratio adjusted for growth. Below 1 might mean the stock is undervalued relative to its growth.",
  pbRatio: "Price-to-Book ratio - compares stock price to the company's net asset value. Lower may indicate undervaluation.",
  psRatio: "Price-to-Sales ratio - stock price relative to revenue. Useful for companies not yet profitable.",
  evToEbitda: "Enterprise Value to EBITDA - measures total company value against operating earnings. Lower is typically better.",

  // Fundamental metrics - Profitability
  profitMargin: "What percentage of revenue the company keeps as profit after all expenses.",
  operatingMargin: "Percentage of revenue left after paying operating costs. Higher means more efficient operations.",
  roe: "Return on Equity - how efficiently the company uses investor money to generate profits.",
  roa: "Return on Assets - how well the company uses all its assets to generate profit.",
  revenueGrowth: "How fast the company's sales are growing compared to last year.",
  earningsGrowth: "How fast the company's profits are growing compared to last year.",

  // Fundamental metrics - Additional
  eps: "Earnings Per Share - the company's profit divided by shares outstanding. Higher is better.",
  dividendYield: "Annual dividend payment as a percentage of stock price. Higher means more income.",
  beta: "Measures stock volatility vs the market. Above 1 = more volatile, below 1 = less volatile.",
  week52High: "The highest price this stock has traded at in the past year.",
  week52Low: "The lowest price this stock has traded at in the past year.",
  analystTarget: "The average price Wall Street analysts expect this stock to reach.",
  debtToEquity: "How much debt the company has compared to what shareholders own. Lower is generally safer.",
  priceTarget: "The average price analysts think this stock should be worth.",

  // Chart explanations
  earningsHistory: "Shows reported earnings per share (EPS) vs analyst estimates over time. Beating estimates is positive.",
  epsGrowth: "Annual earnings per share trend - shows how profitability has grown over the years.",
  incomeStatement: "Revenue and profit over time. Consistent growth indicates a healthy business.",
  marginTrends: "Profit margins over time. Stable or rising margins suggest pricing power and efficiency.",
  cashFlow: "Money flowing in and out of the business. Positive free cash flow means the company generates excess cash.",
  revenueProfit: "Compares total sales (revenue) to what the company keeps (profit). Gap shows operating costs.",

  // Share Dilution & Capital Allocation
  shareDilution: "Compares stock buybacks (good for shareholders) vs stock-based compensation (dilutes shareholders). Net positive = returning value.",
  sharesOutstanding: "Total shares in circulation. Decreasing = buybacks (good), Increasing = dilution from SBC or stock issuance (reduces your ownership).",
  stockBasedCompensation: "Stock paid to employees instead of cash. This dilutes existing shareholders even if the company is buying back stock.",
  netShareholderReturn: "Buybacks minus stock-based compensation. Shows if shareholders are actually benefiting or being diluted despite buyback announcements.",
  buybacks: "When a company repurchases its own shares, reducing shares outstanding and increasing your ownership percentage.",

  // Signals
  signal: "BUY signals occur when sentiment is LOW but fundamentals are STRONG - the market is wrong. SELL signals occur when sentiment is HIGH (stretched) - no upside left. Not financial advice.",
  keyThemes: "The main topics people are discussing about this stock on social media.",

  // Historical
  historicalTrends: "See how sentiment and the disconnect score have changed over time for this stock.",

  // Price
  currentPrice: "The most recent trading price of this stock.",
};
